import { UIEvent, useEffect, useRef, useState } from 'react';

// Cross-browser compatible UUID generation
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export default function Picker({
  optionHeight = 44,
  defaultValue,
  renderOption,
  visibleOptionNum = 5,
  options = [],
  className = '',
  onChange = () => {},
  optionsFieldNames = {
    label: 'label',
    value: 'value',
  },
}: {
  optionHeight?: number | string;
  defaultValue?: any;
  renderOption?: (option: any, index: number) => React.ReactNode;
  visibleOptionNum?: number;
  options: any[];
  optionsFieldNames?: {
    label: string;
    value: string;
  };
  onChange?: (value?: any, option?: any, currentIndex?: number) => void;
  className?: string;
}) {
  const [visibleOptionNumber] = useState(
    visibleOptionNum % 2 !== 0 ? visibleOptionNum : visibleOptionNum + 1,
  );
  const [UUID] = useState(generateUUID());
  const [itemHeight] = useState<string>(
    typeof optionHeight === 'number' ? `${optionHeight}px` : (optionHeight as string),
  );
  const setEmptyArr = () => {
    const arr: number[] = [];
    for (let i = 0; i < (visibleOptionNumber - 1) / 2; i++) {
      arr.push(i);
    }
    return arr;
  };
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emptyItems] = useState<number[]>(setEmptyArr());
  const scrollTo = (value: any) => {
    if (scrollRef) {
      let index = 0;
      for (const i in options) {
        if (options[i][optionsFieldNames.label] === value) {
          index = Number(i);
          setCurrentIndex(index);
          break;
        }
      }
      const _itemHeight = document.getElementById(`picker-option-0-${UUID}`)?.offsetHeight || 44;
      const scrollTop = index * _itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  };
  let timer = null;
  const onScrollHandle = (event: UIEvent<HTMLUListElement>) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      const { target } = event;
      const _itemHeight = document.getElementById(`picker-option-0-${UUID}`)?.offsetHeight || 44;
      let index = Math.floor((target as HTMLUListElement).scrollTop / _itemHeight);
      if (index < 0) index = 0;
      if (index > options.length - 1) index = options.length - 1;
      setCurrentIndex(index);
      onChange(options[index][optionsFieldNames.value], options[index], index);
    }, 100);
  };
  const scrollRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    scrollTo(defaultValue);
  }, []);
  return (
    <div className={`picker-container pl4 pr4 relative ${className}`}>
      <ul
        ref={scrollRef}
        id={`Picker-scroll-${UUID}`}
        className="picker-container-inner overflow-auto snap-y snap-mandatory scrollbar-hidden"
        style={{
          height: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * visibleOptionNumber}${itemHeight.replace(/[0-9]/g, '')}`,
          paddingBottom: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * ((visibleOptionNumber - 1) / 2)}${itemHeight.replace(/[0-9]/g, '')}`,
        }}
        onScroll={onScrollHandle}
      >
        {emptyItems.map((item) => (
          <li
            key={`empty-${item}`}
            style={{
              height: itemHeight,
            }}
            className="snap-start w-full block"
          />
        ))}
        {options.map((option, index) => (
          <li
            style={{
              height: itemHeight,
            }}
            key={option[optionsFieldNames.label]}
            id={`picker-option-${index}-${UUID}`}
            className={`snap-start w-full overflow-hidden ${currentIndex === index && 'active bg-[#f3f6f7]'} current-value-picker`}
            data-value={option[optionsFieldNames.label]}
          >
            {renderOption ? (
              renderOption(option, index)
            ) : (
              <div
                className={`pt-8 pb-8 flex text-center items-center justify-center text-16 ${currentIndex === index ? 'text-[18px]' : ''}`}
                style={{
                  height: itemHeight,
                }}
              >
                {option[optionsFieldNames.label]}
              </div>
            )}
          </li>
        ))}
      </ul>
      <div
        className="absolute top-0 left-0 right-0 bg-primary-white opacity-80 pointer-events-none"
        style={{
          height: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * ((visibleOptionNumber - 1) / 2)}${itemHeight.replace(/[0-9]/g, '')}`,
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 bg-primary-white opacity-80 pointer-events-none"
        style={{
          height: `${parseFloat(itemHeight.replace(/[^0-9]/g, '')) * ((visibleOptionNumber - 1) / 2)}${itemHeight.replace(/[0-9]/g, '')}`,
        }}
      />
    </div>
  );
}
